import React, { useCallback, useRef, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import {
  clamp,
  hexToHsv,
  hsvToHex,
  normalizeHex,
} from '../utils/colorNuancier';

interface ColorNuancierPickerProps {
  value: string;
  onChange: (hex: string) => void;
  onConfirm: () => void;
}

const ColorNuancierPicker: React.FC<ColorNuancierPickerProps> = ({ value, onChange, onConfirm }) => {
  const normalizedValue = normalizeHex(value);
  const hsv = hexToHsv(normalizedValue);

  const svRef = useRef<HTMLDivElement>(null);
  const hueRef = useRef<HTMLDivElement>(null);
  const [draggingSv, setDraggingSv] = useState(false);
  const [draggingHue, setDraggingHue] = useState(false);

  const updateSvFromPointer = useCallback(
    (clientX: number, clientY: number) => {
      const rect = svRef.current?.getBoundingClientRect();
      if (!rect) return;
      const s = clamp(((clientX - rect.left) / rect.width) * 100, 0, 100);
      const v = clamp((1 - (clientY - rect.top) / rect.height) * 100, 0, 100);
      onChange(hsvToHex(hsv.h, s, v));
    },
    [hsv.h, onChange],
  );

  const updateHueFromPointer = useCallback(
    (clientX: number) => {
      const rect = hueRef.current?.getBoundingClientRect();
      if (!rect) return;
      const h = clamp(((clientX - rect.left) / rect.width) * 360, 0, 360);
      onChange(hsvToHex(h, hsv.s, hsv.v));
    },
    [hsv.s, hsv.v, onChange],
  );

  React.useEffect(() => {
    if (!draggingSv && !draggingHue) return;

    const handleMove = (event: PointerEvent) => {
      if (draggingSv) updateSvFromPointer(event.clientX, event.clientY);
      if (draggingHue) updateHueFromPointer(event.clientX);
    };
    const handleUp = () => {
      setDraggingSv(false);
      setDraggingHue(false);
    };

    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', handleUp);
    return () => {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleUp);
    };
  }, [draggingSv, draggingHue, updateHueFromPointer, updateSvFromPointer]);

  const hueColor = hsvToHex(hsv.h, 100, 100);

  return (
    <Box>
      <Typography variant="body2" sx={{ mb: 1.5, fontWeight: 600 }}>
        Sélecteur HSV
      </Typography>

      <Box
        ref={svRef}
        onPointerDown={(event) => {
          setDraggingSv(true);
          updateSvFromPointer(event.clientX, event.clientY);
        }}
        sx={{
          position: 'relative',
          width: '100%',
          height: 160,
          borderRadius: 1.5,
          cursor: 'crosshair',
          border: '1px solid',
          borderColor: 'divider',
          background: `linear-gradient(to top, #000, transparent), linear-gradient(to right, #fff, ${hueColor})`,
          touchAction: 'none',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            left: `${hsv.s}%`,
            top: `${100 - hsv.v}%`,
            width: 14,
            height: 14,
            borderRadius: '50%',
            border: '2px solid #fff',
            boxShadow: '0 0 0 1px rgba(0,0,0,0.35)',
            transform: 'translate(-50%, -50%)',
            pointerEvents: 'none',
          }}
        />
      </Box>

      <Box
        ref={hueRef}
        onPointerDown={(event) => {
          setDraggingHue(true);
          updateHueFromPointer(event.clientX);
        }}
        sx={{
          mt: 1.25,
          width: '100%',
          height: 14,
          borderRadius: 999,
          cursor: 'pointer',
          border: '1px solid',
          borderColor: 'divider',
          background:
            'linear-gradient(to right, #f00 0%, #ff0 17%, #0f0 33%, #0ff 50%, #00f 67%, #f0f 83%, #f00 100%)',
          position: 'relative',
          touchAction: 'none',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            left: `${(hsv.h / 360) * 100}%`,
            top: '50%',
            width: 16,
            height: 16,
            borderRadius: '50%',
            border: '2px solid #fff',
            boxShadow: '0 0 0 1px rgba(0,0,0,0.35)',
            transform: 'translate(-50%, -50%)',
            pointerEvents: 'none',
            bgcolor: hueColor,
          }}
        />
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mt: 1.5, mb: 1.5 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 1,
            bgcolor: normalizedValue,
            border: '1px solid',
            borderColor: 'divider',
            flexShrink: 0,
          }}
        />
        <Typography variant="caption" color="text.secondary">
          H {Math.round(hsv.h)}° · S {Math.round(hsv.s)}% · V {Math.round(hsv.v)}%
        </Typography>
      </Box>

      <Button fullWidth variant="contained" onClick={onConfirm}>
        Appliquer cette couleur
      </Button>
    </Box>
  );
};

export default ColorNuancierPicker;
